/**
 * WebViewAuth.js
 * Handles authentication when the app is running inside a React Native WebView.
 */
import { CONFIG } from "../../config";

/**
 * Checks if the application is running inside a React Native WebView
 * @returns {boolean} True if running in React Native app
 */
export const isRunningInApp = () => {
  return !!window.isInApp;
};

/**
 * Initializes WebView authentication handlers
 * Should be called early in the application lifecycle
 */
export const initWebViewAuth = () => {
  if (isRunningInApp()) {
    console.log("React Native WebView detected: setting up authentication");
    setupAuthHandlers();
  } else {
    console.log("Running in standard browser environment");
  }
};

/**
 * Sets up authentication event handlers for React Native communication
 */
const setupAuthHandlers = () => {
  // Handler for when the app sends authentication credentials
  window.onAppAuthenticated = function (credentials) {
    console.log("Received authentication from React Native", credentials);

    if (credentials && credentials.accessToken && credentials.idToken) {
      // Store tokens for API calls
      localStorage.setItem("access_token", credentials.accessToken);
      localStorage.setItem("id_token", credentials.idToken);

      // Validate with backend
      validateTokenWithBackend(credentials.accessToken, credentials.idToken);
    } else {
      console.error("Invalid credentials received from app");
    }
  };

  // Trigger app login when requested from web
  if (window.requestAppLogin) {
    console.log("App login handler available");
  }
};

/**
 * Validates tokens with your backend API
 * @param {string} accessToken The Auth0 access token
 * @param {string} idToken The Auth0 ID token
 */
const validateTokenWithBackend = (accessToken, idToken) => {
  fetch(`${CONFIG.API_URL}/auth/validate-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      accessToken: accessToken,
      idToken: idToken,
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      console.log("Token validation successful:", data);

      // Dispatch an authentication success event
      const event = new CustomEvent("webviewAuthSuccess", {
        detail: { user: data.user },
      });
      window.dispatchEvent(event);

      // Reload the app to apply the new authentication state
      window.location.reload();
    })
    .catch((error) => {
      console.error("Token validation failed:", error);

      // Dispatch an authentication error event
      const event = new CustomEvent("webviewAuthError", {
        detail: { error: "Failed to validate authentication tokens" },
      });
      window.dispatchEvent(event);
    });
};

/**
 * Requests login through the React Native app
 * @returns {boolean} True if the request was handled
 */
export const requestAppLogin = () => {
  if (isRunningInApp() && window.requestAppLogin) {
    console.log("Requesting login through React Native app");
    window.requestAppLogin();
    return true;
  } else {
    console.log("App login not available in this environment");
    return false;
  }
};

/**
 * Adds app login capability to a button
 * @param {HTMLElement} button The button element to enable
 */
export const enableAppLoginButton = (button) => {
  if (button && isRunningInApp() && window.requestAppLogin) {
    button.addEventListener("click", function (e) {
      e.preventDefault();
      requestAppLogin();
    });
    button.removeAttribute("disabled");
  }
};
